import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { Filters } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Filter, FilterChangeData } from '@shared/components/filters/models';
import { cloneDeep, isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { resultsViewSettings, ViewSettings, isExtension } from '@local/common-web';

@UntilDestroy()
@Component({
  selector: 'chat-filters-box',
  templateUrl: './chat-filters-box.component.html',
  styleUrls: ['./chat-filters-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatFiltersBoxComponent implements OnInit {
  private readonly FILTERS_SESSION_NAME = 'chat';
  private currentFilters: Filters.Values;
  private filtersSub: Subscription;
  private viewSettings: ViewSettings;
  private _disabled = false;
  private _currentAssistant: string;
  filters: Filter[];
  isExtension = isExtension();

  get disabled() {
    return this._disabled;
  }

  @Input() set disabled(value: boolean) {
    if (value === this._disabled) {
      return;
    }
    this._disabled = value;
    this.filters = this.filters?.map((f) => ({ ...f, disabled: value }));
    this.cdr.markForCheck();
  }

  @Input() set currentAssistant(value: string) {
    this._currentAssistant = value;
    this.filters = [];
    this.cdr.markForCheck();
    this.updateFilters();
  }

  @Output() filterChange = new EventEmitter<void>();

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private filtersService: FiltersService,
    private resultsService: ResultsService
  ) {
    this.viewSettings = resultsViewSettings['chat'];
  }

  ngOnInit() {
    this.initFilters();
  }

  private initFilters() {
    this.filtersService.allFilters$.pipe(untilDestroyed(this)).subscribe((filters) => {
      const newFilters = cloneDeep(filters);
      if (!isEqual(newFilters, this.currentFilters)) {
        this.currentFilters = newFilters;
        this.updateFilters();
      }
    });
  }

  private updateFilters() {
    this.filtersSub?.unsubscribe();
    const { filters, moreFilters } = this.viewSettings;
    this.filtersSub = this.filtersService
      .getFloatingResultsFilters({
        sessionName: this.FILTERS_SESSION_NAME,
        moreFilters: { ...moreFilters, onlyIcon: this.isExtension },
        baseFilters: filters,
        typeSuggestedFilters: [],
        moreFilterValuesViewDetails: {
          position: 'Main',
        },
        assistantId: this._currentAssistant,
      })
      .subscribe((res) => {
        this.filters = res.map((f) => ({ ...f, disabled: this.disabled }));
        this.cdr.markForCheck();
      });
  }

  onFilterChange(data: FilterChangeData) {
    this.filterChange.emit();
    this.resultsService.handleDataChanges(data);
  }

  clearAllFilters() {
    this.filtersService.removeAllFilters('all', false);
  }
}
